import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { History, AlertTriangle, Loader2, Info, RotateCw } from 'lucide-react';
import ReadingForm from './ReadingForm';
import ModalShell, { ModalFooter } from '../common/ModalShell';
import { fetchVisitReadings, recordReading, correctReading } from '../../redux/checkSetSlice';
import { deleteDraft, checkSection } from '../../redux/appointmentDraftSlice';
import {
    offeredSets, formValuesFrom, readingPayload, validateReadingValues,
    summarizeItems, isVisitOpenForStaff,
} from '../../utils/checkSets';
import { formatDay } from '../health/healthFormat';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// "RECORD READINGS" — the desk's way in (nurse Priya before the visit, the
// hospital admin of a small clinic, or a doctor from the list).
//
// Offered sets are the SERVER's answer for this caller: staff and the admin
// get the sets flagged "Staff may record" (a doctor's own only while that
// doctor's switch is on); the visit's doctor gets all of theirs and the
// hospital's. One line of the patient's previous reading of the chosen set is
// shown ("Last …", §8-2) and nothing else of the record.
//
// A set that already has a live reading on this visit opens pre-filled, and
// saving it is a CORRECTION (a new row; the old one is kept, superseded).
// Staff write directly — no drafts (those are the doctor's).
//
// A DOCTOR saving here retires their own draft of that set (`checks.<setId>`),
// exactly as the visit block's own Save does. Otherwise a value typed in the
// visit earlier and left as a draft would come back on the next open as a
// pending correction ("Unsaved draft restored"), and "Save & complete" would
// commit it over the reading just saved here. Staff and the admin have no
// drafts, so nothing is deleted for them.

function RecordReadingsModal({ isOpen, appointment, initialSetId = null, onClose, onSaved }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const role = (typeof localStorage !== 'undefined' && localStorage.getItem('Role')) || '';
    const appointmentId = appointment?.appointmentId || null;
    const visit = useSelector((state) => (appointmentId ? state.checkSets?.visits?.[appointmentId] : null));
    const saving = useSelector((state) => Boolean(appointmentId && state.checkSets?.recording?.[appointmentId]));
    const data = visit?.data || null;
    const sets = useMemo(() => offeredSets(data), [data]);

    const [setId, setSetId] = useState(null);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const closeRef = useRef(null);
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        if (!isOpen || !appointmentId) return;
        setSetId(null);
        setValues({});
        setErrors({});
        setServerError('');
        dispatch(fetchVisitReadings({ appointmentId, force: true }));
    }, [isOpen, appointmentId, dispatch]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
        window.addEventListener('keydown', onKey);
        // closeRef holds the frame; focus its close button.
        closeRef.current?.querySelector('[data-testid="record-readings-close"]')?.focus();
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen]);

    const live = useMemo(() => (data?.readings || []).find((r) => r.setId === setId) || null, [data, setId]);
    const set = useMemo(() => sets.find((s) => s.setId === setId) || null, [sets, setId]);

    const choose = (id) => {
        const next = sets.find((s) => s.setId === id);
        const existing = (data?.readings || []).find((r) => r.setId === id);
        setSetId(id);
        setValues(next ? formValuesFrom(next.fields, existing?.values) : {});
        setErrors({});
        setServerError('');
    };

    // Pre-select once the offer is known: the set the caller asked for, or
    // the only one there is.
    useEffect(() => {
        if (!isOpen || setId || sets.length === 0) return;
        if (initialSetId && sets.some((s) => s.setId === initialSetId)) choose(initialSetId);
        else if (sets.length === 1) choose(sets[0].setId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, sets, initialSetId]);

    if (!isOpen || !appointment || typeof document === 'undefined') return null;

    const status = data?.appointmentStatus || appointment.status || null;
    // The desk's rule, the same for every role (VisitReadingsPanel, the list).
    const closed = !isVisitOpenForStaff(status);
    const previous = setId ? data?.previous?.[setId] : null;
    const lang = i18n.language;

    const onChange = (key, value) => {
        setValues((prev) => {
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
        });
        setErrors((prev) => {
            if (!prev[key] && !prev._empty) return prev;
            const next = { ...prev };
            delete next[key];
            delete next._empty;
            return next;
        });
    };

    const save = async () => {
        if (!set || saving || closed) return;
        const found = validateReadingValues(set.fields, values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }
        const payload = readingPayload(set.fields, values);
        setServerError('');
        const res = live
            ? await dispatch(correctReading({ readingId: live.readingId, appointmentId, values: payload }))
            : await dispatch(recordReading({ appointmentId, setId: set.setId, values: payload }));
        const ok = live ? correctReading.fulfilled.match(res) : recordReading.fulfilled.match(res);
        if (ok) {
            // Best-effort and idempotent server-side; a failure leaves the
            // block's own restore rule to drop the stale draft.
            if (role === 'doctor') dispatch(deleteDraft({ appointmentId, section: checkSection(set.setId) }));
            toast.success(live ? t('checkSets.correction_saved') : t('checkSets.reading_saved'));
            onSaved?.(res.payload?.reading || null);
            onClose?.();
            return;
        }
        const refusal = res.payload || {};
        const message = serverReason(refusal, t('checkSets.reading_save_failed'), lang);
        if (refusal.field && set.fields.some((f) => f.key === refusal.field)) {
            setErrors((prev) => ({ ...prev, [refusal.field]: message }));
        } else {
            setServerError(message);
        }
        // Someone else saved this set meanwhile: show what is there now.
        if (refusal.code === 'READING_SUPERSEDED' || refusal.code === 'READING_CONFLICT') {
            dispatch(fetchVisitReadings({ appointmentId, force: true }));
            setSetId(null);
        }
    };

    const loading = !data && (visit?.loading || !visit);
    const loadError = !data && visit?.error;

    return createPortal(
        // Click on the dimmed page closes, as before the shared frame.
        <div ref={closeRef} onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) onClose(); }}>
            <ModalShell
                title={t('checkSets.record_title')}
                onClose={onClose}
                closeLabel={t('checkSets.close')}
                size="2xl"
                zClass="z-[60]"
                testId="record-readings-modal-root"
                closeTestId="record-readings-close"
                contentTestId="record-readings-modal"
                footer={(
                    <ModalFooter
                        onCancel={onClose}
                        cancelLabel={t('checkSets.cancel')}
                        onSubmit={save}
                        submitLabel={t('checkSets.save_reading')}
                        busy={saving}
                        disabled={!set || closed}
                        cancelTestId="record-readings-cancel"
                        submitTestId="record-readings-save"
                    />
                )}
            >
                    {appointment.patientName && (
                        <p data-testid="record-readings-patient" className="-mt-1 mb-3 truncate text-xs text-gray-500">
                            {t('checkSets.patient_line', { name: appointment.patientName })}
                        </p>
                    )}
                    {loading && (
                        <div data-testid="record-readings-loading" className="flex items-center justify-center gap-2 py-10 text-sm text-[#2D8A8A]">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            {t('checkSets.loading')}
                        </div>
                    )}

                    {loadError && (
                        <div role="alert" data-testid="record-readings-load-error" className="flex flex-col items-center gap-2 rounded-xl border border-red-100 bg-red-50/60 p-4 text-center">
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                                {serverReason(visit.error, t('checkSets.readings_load_failed'), lang)}
                            </p>
                            <button
                                type="button"
                                onClick={() => dispatch(fetchVisitReadings({ appointmentId, force: true }))}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            >
                                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                                {t('checkSets.retry')}
                            </button>
                        </div>
                    )}

                    {data && closed && (
                        <p data-testid="record-readings-closed" className="mb-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-800">
                            <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {t('checkSets.visit_closed')}
                        </p>
                    )}

                    {data && sets.length === 0 && (
                        <p data-testid="record-readings-none-offered" className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                            {t('checkSets.no_sets_offered')}
                        </p>
                    )}

                    {data && sets.length > 0 && (
                        <>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">{t('checkSets.choose_set')}</p>
                            <div role="radiogroup" aria-label={t('checkSets.choose_set')} className="mb-4 flex flex-wrap gap-2" data-testid="record-readings-sets">
                                {sets.map((s) => {
                                    const on = s.setId === setId;
                                    const done = (data.readings || []).some((r) => r.setId === s.setId);
                                    return (
                                        <button
                                            key={s.setId}
                                            type="button"
                                            role="radio"
                                            aria-checked={on}
                                            data-testid={`record-readings-set-${s.setId}`}
                                            onClick={() => choose(s.setId)}
                                            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${on
                                                ? 'border-[#2D8A8A] bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow'
                                                : 'border-gray-200 bg-white text-gray-700 hover:border-[#4ABDBD] hover:text-[#2D8A8A]'}`}
                                        >
                                            {s.name}
                                            {done && <span className={`rounded-full px-1.5 py-px text-[10px] font-bold ${on ? 'bg-white/90 text-[#2D8A8A]' : 'bg-[#4ABDBD]/15 text-[#2D8A8A]'}`}>{t('checkSets.form_correction')}</span>}
                                        </button>
                                    );
                                })}
                            </div>

                            {set && (
                                <div className="flex flex-col gap-3">
                                    {previous?.items?.length > 0 && (
                                        <p data-testid="record-readings-last" className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                                            <History className="mt-px h-3.5 w-3.5 shrink-0 text-[#2D8A8A]" aria-hidden="true" />
                                            <span>{t('checkSets.last_line', { date: formatDay(previous.recordedAt), summary: summarizeItems(previous.items, t) })}</span>
                                        </p>
                                    )}
                                    {live && (
                                        <p data-testid="record-readings-correcting" className="text-xs font-medium text-[#2D8A8A]">{t('checkSets.already_recorded')}</p>
                                    )}
                                    <ReadingForm
                                        set={set}
                                        values={values}
                                        errors={errors}
                                        onChange={onChange}
                                        disabled={saving || closed}
                                        idPrefix="record-readings"
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {serverError && (
                        <p role="alert" data-testid="record-readings-server-error" className="mt-3 flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-700">
                            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {serverError}
                        </p>
                    )}
            </ModalShell>
        </div>,
        document.body
    );
}

export default RecordReadingsModal;
